import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

const EditorContainer = styled.div`
  position: relative;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 400px;
  padding: 12px;
  border: none;
  outline: none;
  background: #fff;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
  line-height: 1.4;
  resize: vertical;
  border-radius: 4px;

  &:focus {
    box-shadow: inset 0 0 0 2px #007bff;
  }

  &.error {
    border: 1px solid #dc3545;
    background: #fff5f5;
  }

  &:disabled {
    background: #f8f9fa;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
  padding: 4px 8px;
  background: #fff5f5;
  border-radius: 3px;
  border: 1px solid #feb2b2;
`;

const HelpText = styled.div`
  color: #6c757d;
  font-size: 12px;
  margin-top: 4px;
  padding: 4px 8px;
  background: #f8f9fa;
  border-radius: 3px;
`;

const ValidationStatus = styled.div<{ $isValid: boolean }>`
  color: ${props => props.$isValid ? '#28a745' : '#dc3545'};
  font-size: 12px;
  margin-top: 4px;
  padding: 4px 8px;
  background: ${props => props.$isValid ? '#f8fff9' : '#fff5f5'};
  border-radius: 3px;
  border: 1px solid ${props => props.$isValid ? '#d4edda' : '#feb2b2'};
`;

// Enhanced Modal/Dialog styles for full-screen editing
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  width: 95vw;
  max-width: 1200px;
  max-height: 95vh;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 20px 24px 16px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  color: #333;
`;

const ModalBody = styled.div`
  padding: 24px;
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const FullScreenTextArea = styled.textarea`
  width: 100%;
  height: 100%;
  min-height: 400px;
  padding: 16px;
  border: 1px solid #ddd;
  outline: none;
  background: #fafafa;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 14px;
  line-height: 1.5;
  resize: none;
  border-radius: 4px;
  flex: 1;

  &:focus {
    border-color: #007bff;
    background: #fff;
  }

  &.error {
    border-color: #dc3545;
    background: #fff5f5;
  }
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e9ecef;
`;

const ToolbarLeft = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ToolbarRight = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 6px 12px;
  border: 1px solid ${props => {
    if (props.$variant === 'primary') return '#007bff';
    if (props.$variant === 'danger') return '#dc3545';
    return '#6c757d';
  }};
  border-radius: 4px;
  background: ${props => {
    if (props.$variant === 'primary') return '#007bff';
    if (props.$variant === 'danger') return '#dc3545';
    return '#fff';
  }};
  color: ${props => props.$variant ? '#fff' : '#6c757d'};
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => {
      if (props.$variant === 'primary') return '#0056b3';
      if (props.$variant === 'danger') return '#c82333';
      return '#e9ecef';
    }};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StatusBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
  font-size: 12px;
  color: #6c757d;
`;

const LineCounter = styled.div`
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  color: #6c757d;
`;

const ErrorList = styled.div`
  max-height: 120px;
  overflow-y: auto;
  margin-top: 8px;
`;

const ErrorItem = styled.div`
  padding: 4px 8px;
  margin-bottom: 4px;
  background: #fff5f5;
  border: 1px solid #feb2b2;
  border-radius: 3px;
  font-size: 12px;
  color: #dc3545;
`;

const TemplateSelector = styled.select`
  padding: 4px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 12px;
  background: white;
`;

interface JsonEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  helpText?: string;
  error?: string;
  disabled?: boolean;
  label?: string;
  fullScreen?: boolean;
  onFullScreenToggle?: () => void;
  showToolbar?: boolean;
  showStatusBar?: boolean;
  validationSchema?: any;
  templates?: { [key: string]: string };
}

const JsonEditor: React.FC<JsonEditorProps> = ({
  value,
  onChange,
  placeholder = '{}',
  helpText,
  error,
  disabled = false,
  label,
  fullScreen = false,
  onFullScreenToggle,
  showToolbar = false,
  showStatusBar = false,
  validationSchema,
  templates = {}
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isValid, setIsValid] = useState(true);
  const [lineCount, setLineCount] = useState(1);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Count lines in the textarea
  useEffect(() => {
    if (value) {
      const lines = value.split('\n').length;
      setLineCount(lines);
    } else {
      setLineCount(1);
    }
  }, [value]);

  // Validate JSON
  const validateJson = (jsonString: string): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    try {
      if (jsonString.trim()) {
        JSON.parse(jsonString);
      }
    } catch (e) {
      errors.push(`JSON Syntax Error: ${e instanceof Error ? e.message : 'Invalid JSON'}`);
    }

    // Additional schema validation if provided
    if (validationSchema && jsonString.trim()) {
      try {
        const parsed = JSON.parse(jsonString);
        // Add custom validation logic here
        if (validationSchema.required && validationSchema.required.length > 0) {
          validationSchema.required.forEach((field: string) => {
            if (!parsed[field]) {
              errors.push(`Missing required field: ${field}`);
            }
          });
        }
      } catch (e) {
        // JSON parsing error already caught above
      }
    }

    return { isValid: errors.length === 0, errors };
  };

  // Validate on value change
  useEffect(() => {
    const validation = validateJson(value);
    setIsValid(validation.isValid);
    setValidationErrors(validation.errors);
  }, [value, validationSchema]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
  };

  const formatJson = () => {
    try {
      if (value.trim()) {
        const formatted = JSON.stringify(JSON.parse(value), null, 2);
        onChange(formatted);
      }
    } catch (e) {
      // Don't format if invalid JSON
    }
  };

  const clearEditor = () => {
    onChange('');
  };

  const insertTemplate = (templateName: string) => {
    if (templates[templateName]) {
      onChange(templates[templateName]);
    }
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const saveModal = () => {
    closeModal();
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch (e) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = value;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  const pasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      onChange(text);
    } catch (e) {
      // Fallback for older browsers
      alert('Please paste your JSON manually');
    }
  };

  const exportJson = () => {
    const blob = new Blob([value], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'prompt-profile.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const importJson = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        onChange(content);
      };
      reader.readAsText(file);
    }
  };

  const renderToolbar = () => (
    <Toolbar>
      <ToolbarLeft>
        <Button onClick={formatJson} disabled={!value.trim()}>
          Format JSON
        </Button>
        <Button onClick={clearEditor} disabled={!value.trim()}>
          Clear
        </Button>
        {Object.keys(templates).length > 0 && (
          <TemplateSelector onChange={(e) => insertTemplate(e.target.value)}>
            <option value="">Load Template...</option>
            {Object.keys(templates).map(key => (
              <option key={key} value={key}>{key}</option>
            ))}
          </TemplateSelector>
        )}
      </ToolbarLeft>
      <ToolbarRight>
        <Button onClick={copyToClipboard} disabled={!value.trim()}>
          Copy
        </Button>
        <Button onClick={pasteFromClipboard}>
          Paste
        </Button>
        <Button onClick={exportJson} disabled={!value.trim()}>
          Export
        </Button>
        <label>
          <input
            type="file"
            accept=".json"
            onChange={importJson}
            style={{ display: 'none' }}
          />
          <Button as="span">Import</Button>
        </label>
        {onFullScreenToggle && (
          <Button onClick={onFullScreenToggle}>
            {fullScreen ? 'Exit Full Screen' : 'Full Screen'}
          </Button>
        )}
      </ToolbarRight>
    </Toolbar>
  );

  const renderStatusBar = () => (
    <StatusBar>
      <div>
        {isValid ? (
          <span style={{ color: '#28a745' }}>✓ Valid JSON</span>
        ) : (
          <span style={{ color: '#dc3545' }}>✗ Invalid JSON</span>
        )}
        {validationErrors.length > 0 && (
          <span style={{ marginLeft: '8px', color: '#dc3545' }}>
            {validationErrors.length} error(s)
          </span>
        )}
      </div>
      <LineCounter>
        {lineCount} line{lineCount !== 1 ? 's' : ''} | {value.length} characters
      </LineCounter>
    </StatusBar>
  );

  const renderErrors = () => (
    validationErrors.length > 0 && (
      <ErrorList>
        {validationErrors.map((error, index) => (
          <ErrorItem key={index}>{error}</ErrorItem>
        ))}
      </ErrorList>
    )
  );

  if (fullScreen) {
    return (
      <ModalOverlay>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>{label || 'JSON Editor'}</ModalTitle>
            <Button onClick={closeModal}>✕</Button>
          </ModalHeader>
          <ModalBody>
            {showToolbar && renderToolbar()}
            <FullScreenTextArea
              ref={textareaRef}
              value={value}
              onChange={handleChange}
              placeholder={placeholder}
              disabled={disabled}
              className={!isValid ? 'error' : ''}
            />
            {renderErrors()}
          </ModalBody>
          {showStatusBar && renderStatusBar()}
          <ModalFooter>
            <Button onClick={closeModal}>Cancel</Button>
            <Button $variant="primary" onClick={saveModal}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </ModalOverlay>
    );
  }

  return (
    <EditorContainer>
      {label && <div style={{ marginBottom: '8px', fontWeight: '500' }}>{label}</div>}
      <TextArea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        className={!isValid ? 'error' : ''}
      />
      {helpText && <HelpText>{helpText}</HelpText>}
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {validationErrors.length > 0 && renderErrors()}
      <ValidationStatus $isValid={isValid}>
        {isValid ? 'Valid JSON' : 'Invalid JSON'}
      </ValidationStatus>
      {onFullScreenToggle && (
        <div style={{ marginTop: '8px' }}>
          <Button onClick={onFullScreenToggle}>Open Full Screen Editor</Button>
        </div>
      )}
    </EditorContainer>
  );
};

export default JsonEditor; 
 